import { useEffect, useRef, useState } from "react";

// ─── Counter hook ─────────────────────────────────────────────────────────────

function parseValue(raw: string): { num: number; prefix: string; suffix: string } {
  // Handles "200K+", "15K+", "50+", "15+"
  const match = raw.match(/^(\D*)(\d+(?:\.\d+)?)(K?[^0-9]*)$/);
  if (!match) return { num: 0, prefix: "", suffix: raw };
  return { prefix: match[1], num: parseFloat(match[2]), suffix: match[3] };
}

function useCountUp(target: number, duration = 1800, shouldStart: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;
    let startTime: number | null = null;
    let raf: number;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // Cubic ease-out for a snappy finish
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [shouldStart, target, duration]);

  return count;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface StatCardProps {
  value: string;
  label: string;
  description: string;
  dark: boolean;
}

const StatCard = ({ value, label, description, dark }: StatCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { num, prefix, suffix } = parseValue(value);
  const count = useCountUp(num, 1800, visible);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`flex flex-col justify-between gap-10 rounded-2xl p-6 xl:p-8 min-h-[240px] ${
        dark
          ? "bg-blue-100 border border-blue-100"
          : "bg-[#FEFAE8] border border-yellow-200"
      }`}
    >
      <p
        className={`text-[40px] sm:text-[48px] xl:text-[60px] font-black leading-none tabular-nums ${
          dark ? "text-yellow-100" : "text-yellow-400"
        }`}
      >
        {prefix}
        {count}
        {suffix}
      </p>
      <div className="flex flex-col gap-2">
        <p className={`font-semibold text-base ${dark ? "text-white" : "text-blue-100"}`}>
          {label}
        </p>
        <p className={`text-sm leading-relaxed ${dark ? "text-white/70" : "text-dark-100"}`}>
          {description}
        </p>
      </div>
    </div>
  );
};

export default StatCard;
